import type { TFunction } from "i18next";

/**
 * Turn an ISO date into a friendly relative string.
 *   <1d   → "Heute, 14:30"
 *   1d    → "Gestern, 09:15"
 *   2–6d  → "Dienstag, 18:42"
 *   7–13d → "vor einer Woche"
 *   14–27 → "vor 2/3 Wochen"
 *   28–59 → "vor einem Monat"
 *   ≥60d  → "vor N Monaten"
 *   ≥1y   → absolutes Datum, z.B. 02.01.2026
 */
export function humanDate(iso: string, t: TFunction, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfD.getTime()) / (24 * 60 * 60 * 1000));

  const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

  if (dayDiff === 0) return t("finder.dateToday", { time });
  if (dayDiff === 1) return t("finder.dateYesterday", { time });
  if (dayDiff >= 2 && dayDiff <= 6) {
    const weekday = d.toLocaleDateString(locale, { weekday: "long" });
    return t("finder.dateWeekday", { weekday, time });
  }
  if (dayDiff >= 7 && dayDiff < 14) return t("finder.dateOneWeekAgo");
  if (dayDiff >= 14 && dayDiff < 28) return t("finder.dateWeeksAgo", { count: Math.floor(dayDiff / 7) });
  if (dayDiff >= 28 && dayDiff < 60) return t("finder.dateOneMonthAgo");
  if (dayDiff >= 60 && dayDiff < 365) return t("finder.dateMonthsAgo", { count: Math.floor(dayDiff / 30) });
  return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function fullTimestamp(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale);
}
